import apiBaseUrl from '../../utils/config.js';
import { authFetch } from '../../utils/authFetch.js';

/**
 * Safely parse an API response that may contain JSON, text, or no body.
 */
async function parseResponse(response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return {
      message: text,
    };
  }
}

/**
 * Convert an unsuccessful backend response into a consistent Error.
 */
function createRequestError(response, data, fallbackMessage) {
  const validationMessage = Array.isArray(data?.errors)
    ? data.errors
        .map((item) => item?.message || item?.defaultMessage || item)
        .filter(Boolean)
        .join(' ')
    : '';

  const message =
    validationMessage ||
    data?.message ||
    data?.error ||
    fallbackMessage ||
    `HTTP ${response.status}`;

  const error = new Error(message);
  error.status = response.status;
  error.data = data;
  return error;
}

function requirePositiveId(value, label) {
  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue <= 0) {
    throw new Error(`A valid ${label} is required.`);
  }

  return numericValue;
}

class ClinicalTemplateAdminService {
  constructor() {
    this.bodyPartBaseUrl = `${apiBaseUrl}/clinical-notes/templates/body-parts`;
    this.painBaseUrl = `${apiBaseUrl}/clinical-notes/templates/pain`;
    this.romBaseUrl = `${apiBaseUrl}/clinical-notes/templates/rom`;
    this.strengthBaseUrl = `${apiBaseUrl}/clinical-notes/templates/strength`;
    this.goalBaseUrl = `${apiBaseUrl}/clinical-notes/templates/goals`;
    this.specialTestBaseUrl = `${apiBaseUrl}/clinical-notes/templates/special-tests`;
    this.palpationBaseUrl = `${apiBaseUrl}/clinical-notes/templates/palpation`;
    this.aggravatingFactorBaseUrl = `${apiBaseUrl}/clinical-notes/templates/aggravating-factors`;
    this.contentBaseUrl = `${apiBaseUrl}/clinical-notes/templates/content`;
    this.treatmentBaseUrl = `${apiBaseUrl}/clinical-notes/templates/treatment`;
    this.insuranceRuleBaseUrl = `${apiBaseUrl}/clinical-notes/templates/insurance-rules`;
    this.providerSignatureBaseUrl = `${apiBaseUrl}/clinical-notes/provider-signatures`;
    this.providerSignatureImageBaseUrl = `${apiBaseUrl}/clinical-notes/provider-signature-images`;
  }

  getPlainHeaders() {
    return {};
  }

  getJsonHeaders() {
    return {
      'Content-Type': 'application/json',
    };
  }

  /* ============================================================
     BODY-PART TEMPLATES
     ============================================================ */

  async getAllBodyPartTemplates() {
    const response = await authFetch(`${this.bodyPartBaseUrl}/all`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Body-part templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveBodyPartTemplates() {
    const response = await authFetch(`${this.bodyPartBaseUrl}/active`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active body-part templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getBodyPartTemplate(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(`${this.bodyPartBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Body-part template could not be loaded.'
      );
    }

    return data || {};
  }

  async getBodyPartTemplateByCode(bodyPartCode) {
    const code = String(bodyPartCode || '').trim();

    if (!code) {
      throw new Error('Body-part code is required.');
    }

    const response = await authFetch(
      `${this.bodyPartBaseUrl}/code/${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Body-part template could not be loaded by code.'
      );
    }

    return data || {};
  }

  async createBodyPartTemplate(request) {
    const response = await authFetch(this.bodyPartBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Body-part template could not be created.'
      );
    }

    return data || {};
  }

  async updateBodyPartTemplate(bodyPartTemplateId, request) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(`${this.bodyPartBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Body-part template could not be updated.'
      );
    }

    return data || {};
  }

  async setBodyPartTemplateActive(bodyPartTemplateId, active) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.bodyPartBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Body-part template could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }

  /* ============================================================
     PAIN TEMPLATES
     ============================================================ */

  async getAllPainTemplates() {
    const response = await authFetch(`${this.painBaseUrl}/all`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Pain templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getAllPainTemplatesByBodyPart(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.painBaseUrl}/body-part/${id}/all`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Pain templates for this body part could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getPainTemplate(painTemplateId) {
    const id = requirePositiveId(painTemplateId, 'pain template ID');

    const response = await authFetch(`${this.painBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Pain template could not be loaded.'
      );
    }

    return data || {};
  }

  async getActivePainTemplate(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.painBaseUrl}/body-part/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active pain template could not be loaded.'
      );
    }

    return data || {};
  }

  async createPainTemplate(request) {
    const response = await authFetch(this.painBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Pain template could not be created.'
      );
    }

    return data || {};
  }

  async updatePainTemplate(painTemplateId, request) {
    const id = requirePositiveId(painTemplateId, 'pain template ID');

    const response = await authFetch(`${this.painBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Pain template could not be updated.'
      );
    }

    return data || {};
  }

  async setPainTemplateActive(painTemplateId, active) {
    const id = requirePositiveId(painTemplateId, 'pain template ID');

    const response = await authFetch(
      `${this.painBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Pain template could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }


  /* ============================================================
     ROM TEMPLATES
     ============================================================ */

  async getAllRomTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.romBaseUrl}/body-part/${id}/all`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'ROM templates for this body part could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveRomTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.romBaseUrl}/body-part/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active ROM templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getRequiredActiveRomTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.romBaseUrl}/body-part/${id}/required`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Required ROM templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveSideSpecificRomTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.romBaseUrl}/body-part/${id}/side-specific`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Side-specific ROM templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getRomTemplate(romTemplateId) {
    const id = requirePositiveId(romTemplateId, 'ROM template ID');

    const response = await authFetch(`${this.romBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'ROM template could not be loaded.'
      );
    }

    return data || {};
  }

  async getRomTemplateByCode(bodyPartTemplateId, romCode) {
    const bodyPartId = requirePositiveId(
      bodyPartTemplateId,
      'body-part template ID'
    );
    const code = String(romCode || '').trim();

    if (!code) {
      throw new Error('ROM code is required.');
    }

    const response = await authFetch(
      `${this.romBaseUrl}/body-part/${bodyPartId}/code/${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'ROM template could not be loaded by code.'
      );
    }

    return data || {};
  }

  async createRomTemplate(request) {
    const response = await authFetch(this.romBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'ROM template could not be created.'
      );
    }

    return data || {};
  }

  async updateRomTemplate(romTemplateId, request) {
    const id = requirePositiveId(romTemplateId, 'ROM template ID');

    const response = await authFetch(`${this.romBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'ROM template could not be updated.'
      );
    }

    return data || {};
  }

  async setRomTemplateActive(romTemplateId, active) {
    const id = requirePositiveId(romTemplateId, 'ROM template ID');

    const response = await authFetch(
      `${this.romBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `ROM template could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }

  /* ============================================================
     STRENGTH TEMPLATES
     ============================================================ */

  async getAllStrengthTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.strengthBaseUrl}/body-part/${id}/all`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Strength templates for this body part could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveStrengthTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.strengthBaseUrl}/body-part/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active strength templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getRequiredActiveStrengthTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.strengthBaseUrl}/body-part/${id}/required`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Required strength templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveSideSpecificStrengthTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.strengthBaseUrl}/body-part/${id}/side-specific`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Side-specific strength templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getStrengthTemplate(strengthTemplateId) {
    const id = requirePositiveId(strengthTemplateId, 'strength template ID');

    const response = await authFetch(`${this.strengthBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Strength template could not be loaded.'
      );
    }

    return data || {};
  }

  async getStrengthTemplateByCode(bodyPartTemplateId, strengthCode) {
    const bodyPartId = requirePositiveId(
      bodyPartTemplateId,
      'body-part template ID'
    );
    const code = String(strengthCode || '').trim();

    if (!code) {
      throw new Error('Strength code is required.');
    }

    const response = await authFetch(
      `${this.strengthBaseUrl}/body-part/${bodyPartId}/code/${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Strength template could not be loaded by code.'
      );
    }

    return data || {};
  }

  async createStrengthTemplate(request) {
    const response = await authFetch(this.strengthBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Strength template could not be created.'
      );
    }

    return data || {};
  }

  async updateStrengthTemplate(strengthTemplateId, request) {
    const id = requirePositiveId(strengthTemplateId, 'strength template ID');

    const response = await authFetch(`${this.strengthBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Strength template could not be updated.'
      );
    }

    return data || {};
  }

  async setStrengthTemplateActive(strengthTemplateId, active) {
    const id = requirePositiveId(strengthTemplateId, 'strength template ID');

    const response = await authFetch(
      `${this.strengthBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Strength template could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }


  /* ============================================================
     GOAL TEMPLATES
     ============================================================ */

  async getAllGoalTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.goalBaseUrl}/body-part/${id}/all`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Goal templates for this body part could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveGoalTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.goalBaseUrl}/body-part/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active goal templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveShortTermGoalTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.goalBaseUrl}/body-part/${id}/short-term`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Short-Term Goal templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveLongTermGoalTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.goalBaseUrl}/body-part/${id}/long-term`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Long-Term Goal templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getRequiredActiveGoalTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.goalBaseUrl}/body-part/${id}/required`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Required goal templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getGoalTemplate(goalTemplateId) {
    const id = requirePositiveId(goalTemplateId, 'goal template ID');

    const response = await authFetch(`${this.goalBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Goal template could not be loaded.'
      );
    }

    return data || {};
  }

  async getGoalTemplateByCode(bodyPartTemplateId, goalCode) {
    const bodyPartId = requirePositiveId(
      bodyPartTemplateId,
      'body-part template ID'
    );
    const code = String(goalCode || '').trim();

    if (!code) {
      throw new Error('Goal code is required.');
    }

    const response = await authFetch(
      `${this.goalBaseUrl}/body-part/${bodyPartId}/code/${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Goal template could not be loaded by code.'
      );
    }

    return data || {};
  }

  async createGoalTemplate(request) {
    const response = await authFetch(this.goalBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Goal template could not be created.'
      );
    }

    return data || {};
  }

  async updateGoalTemplate(goalTemplateId, request) {
    const id = requirePositiveId(goalTemplateId, 'goal template ID');

    const response = await authFetch(`${this.goalBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Goal template could not be updated.'
      );
    }

    return data || {};
  }

  async setGoalTemplateActive(goalTemplateId, active) {
    const id = requirePositiveId(goalTemplateId, 'goal template ID');

    const response = await authFetch(
      `${this.goalBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Goal template could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }


  /* ============================================================
     SPECIAL-TEST TEMPLATES
     ============================================================ */

  async getAllSpecialTestTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.specialTestBaseUrl}/body-part/${id}/all`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Special-test templates for this body part could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveSpecialTestTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.specialTestBaseUrl}/body-part/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active special-test templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getRequiredActiveSpecialTestTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.specialTestBaseUrl}/body-part/${id}/required`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Required special-test templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveSideSpecificSpecialTestTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.specialTestBaseUrl}/body-part/${id}/side-specific`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Side-specific special-test templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getSpecialTestTemplate(specialTestTemplateId) {
    const id = requirePositiveId(
      specialTestTemplateId,
      'special-test template ID'
    );

    const response = await authFetch(`${this.specialTestBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Special-test template could not be loaded.'
      );
    }

    return data || {};
  }

  async getSpecialTestTemplateByCode(bodyPartTemplateId, testCode) {
    const bodyPartId = requirePositiveId(
      bodyPartTemplateId,
      'body-part template ID'
    );
    const code = String(testCode || '').trim();

    if (!code) {
      throw new Error('Special-test code is required.');
    }

    const response = await authFetch(
      `${this.specialTestBaseUrl}/body-part/${bodyPartId}/code/${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Special-test template could not be loaded by code.'
      );
    }

    return data || {};
  }

  async createSpecialTestTemplate(request) {
    const response = await authFetch(this.specialTestBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Special-test template could not be created.'
      );
    }

    return data || {};
  }

  async updateSpecialTestTemplate(specialTestTemplateId, request) {
    const id = requirePositiveId(
      specialTestTemplateId,
      'special-test template ID'
    );

    const response = await authFetch(`${this.specialTestBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Special-test template could not be updated.'
      );
    }

    return data || {};
  }

  async setSpecialTestTemplateActive(specialTestTemplateId, active) {
    const id = requirePositiveId(
      specialTestTemplateId,
      'special-test template ID'
    );

    const response = await authFetch(
      `${this.specialTestBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Special-test template could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }


  /* ============================================================
     PALPATION TEMPLATES
     ============================================================ */

  async getAllPalpationTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.palpationBaseUrl}/body-part/${id}/all`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Palpation templates for this body part could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActivePalpationTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.palpationBaseUrl}/body-part/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active palpation templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getRequiredActivePalpationTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.palpationBaseUrl}/body-part/${id}/required`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Required palpation templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveSideSpecificPalpationTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.palpationBaseUrl}/body-part/${id}/side-specific`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Side-specific palpation templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getPalpationTemplate(palpationTemplateId) {
    const id = requirePositiveId(
      palpationTemplateId,
      'palpation template ID'
    );

    const response = await authFetch(`${this.palpationBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Palpation template could not be loaded.'
      );
    }

    return data || {};
  }

  async getPalpationTemplateByCode(bodyPartTemplateId, palpationCode) {
    const bodyPartId = requirePositiveId(
      bodyPartTemplateId,
      'body-part template ID'
    );
    const code = String(palpationCode || '').trim();

    if (!code) {
      throw new Error('Palpation code is required.');
    }

    const response = await authFetch(
      `${this.palpationBaseUrl}/body-part/${bodyPartId}/code/${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Palpation template could not be loaded by code.'
      );
    }

    return data || {};
  }

  async createPalpationTemplate(request) {
    const response = await authFetch(this.palpationBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Palpation template could not be created.'
      );
    }

    return data || {};
  }

  async updatePalpationTemplate(palpationTemplateId, request) {
    const id = requirePositiveId(
      palpationTemplateId,
      'palpation template ID'
    );

    const response = await authFetch(`${this.palpationBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Palpation template could not be updated.'
      );
    }

    return data || {};
  }

  async setPalpationTemplateActive(palpationTemplateId, active) {
    const id = requirePositiveId(
      palpationTemplateId,
      'palpation template ID'
    );

    const response = await authFetch(
      `${this.palpationBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Palpation template could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }


  /* ============================================================
     AGGRAVATING-FACTOR TEMPLATES
     ============================================================ */

  async getAllAggravatingFactorTemplates() {
    const response = await authFetch(`${this.aggravatingFactorBaseUrl}/all`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Aggravating-factor templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getAvailableAggravatingFactorTemplates(bodyPartTemplateId = null) {
    const query =
      bodyPartTemplateId === null ||
      bodyPartTemplateId === undefined ||
      String(bodyPartTemplateId).trim() === ''
        ? ''
        : `?bodyPartTemplateId=${requirePositiveId(
            bodyPartTemplateId,
            'body-part template ID'
          )}`;

    const response = await authFetch(
      `${this.aggravatingFactorBaseUrl}/available${query}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Available aggravating-factor templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async createAggravatingFactorTemplate(request) {
    const response = await authFetch(this.aggravatingFactorBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Aggravating-factor template could not be created.'
      );
    }

    return data || {};
  }

  async updateAggravatingFactorTemplate(templateId, request) {
    const id = requirePositiveId(templateId, 'aggravating-factor template ID');
    const response = await authFetch(`${this.aggravatingFactorBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Aggravating-factor template could not be updated.'
      );
    }

    return data || {};
  }

  async setAggravatingFactorTemplateActive(templateId, active) {
    const id = requirePositiveId(templateId, 'aggravating-factor template ID');
    const response = await authFetch(
      `${this.aggravatingFactorBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Aggravating-factor template could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }


  /* ============================================================
     CLINICAL CONTENT TEMPLATES
     ============================================================ */

  async getAllClinicalContentTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.contentBaseUrl}/body-part/${id}/all`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Clinical content templates for this body part could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveClinicalContentTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.contentBaseUrl}/body-part/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active clinical content templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getRequiredActiveClinicalContentTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.contentBaseUrl}/body-part/${id}/required`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Required clinical content templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveEditableClinicalContentTemplates(bodyPartTemplateId) {
    const id = requirePositiveId(bodyPartTemplateId, 'body-part template ID');

    const response = await authFetch(
      `${this.contentBaseUrl}/body-part/${id}/editable`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Editable clinical content templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getClinicalContentTemplate(clinicalContentTemplateId) {
    const id = requirePositiveId(
      clinicalContentTemplateId,
      'clinical content template ID'
    );

    const response = await authFetch(`${this.contentBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Clinical content template could not be loaded.'
      );
    }

    return data || {};
  }

  async getClinicalContentTemplateByCode(bodyPartTemplateId, contentCode) {
    const bodyPartId = requirePositiveId(
      bodyPartTemplateId,
      'body-part template ID'
    );
    const code = String(contentCode || '').trim();

    if (!code) {
      throw new Error('Content code is required.');
    }

    const response = await authFetch(
      `${this.contentBaseUrl}/body-part/${bodyPartId}/code/${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Clinical content template could not be loaded by code.'
      );
    }

    return data || {};
  }

  async createClinicalContentTemplate(request) {
    const response = await authFetch(this.contentBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Clinical content template could not be created.'
      );
    }

    return data || {};
  }

  async updateClinicalContentTemplate(clinicalContentTemplateId, request) {
    const id = requirePositiveId(
      clinicalContentTemplateId,
      'clinical content template ID'
    );

    const response = await authFetch(`${this.contentBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Clinical content template could not be updated.'
      );
    }

    return data || {};
  }

  async setClinicalContentTemplateActive(clinicalContentTemplateId, active) {
    const id = requirePositiveId(
      clinicalContentTemplateId,
      'clinical content template ID'
    );

    const response = await authFetch(
      `${this.contentBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Clinical content template could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }


  /* ============================================================
     TREATMENT TEMPLATES
     ============================================================ */

  async getTreatmentTemplate(treatmentTemplateId) {
    const id = requirePositiveId(
      treatmentTemplateId,
      'treatment template ID'
    );

    const response = await authFetch(`${this.treatmentBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Treatment template could not be loaded.'
      );
    }

    return data || {};
  }

  async getTreatmentTemplateByCode(treatmentCode) {
    const code = String(treatmentCode || '').trim();

    if (!code) {
      throw new Error('Treatment code is required.');
    }

    const response = await authFetch(
      `${this.treatmentBaseUrl}/code/${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Treatment template could not be loaded by code.'
      );
    }

    return data || {};
  }

  async getActiveTreatmentTemplatesByBodyPart(bodyPartTemplateId) {
    const id = requirePositiveId(
      bodyPartTemplateId,
      'body-part template ID'
    );

    const response = await authFetch(
      `${this.treatmentBaseUrl}/body-part/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active treatment templates for this body part could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveTreatmentTemplatesByInsuranceRule(insuranceNoteRuleId) {
    const id = requirePositiveId(
      insuranceNoteRuleId,
      'insurance-note rule ID'
    );

    const response = await authFetch(
      `${this.treatmentBaseUrl}/insurance-rule/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active treatment templates for this insurance rule could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveTreatmentTemplatesByBodyPartAndInsuranceRule(
    bodyPartTemplateId,
    insuranceNoteRuleId
  ) {
    const bodyPartId = requirePositiveId(
      bodyPartTemplateId,
      'body-part template ID'
    );
    const insuranceRuleId = requirePositiveId(
      insuranceNoteRuleId,
      'insurance-note rule ID'
    );

    const response = await authFetch(
      `${this.treatmentBaseUrl}/body-part/${bodyPartId}/insurance-rule/${insuranceRuleId}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active treatment templates for this body part and insurance rule could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveCopyForwardTreatmentTemplates() {
    const response = await authFetch(
      `${this.treatmentBaseUrl}/copy-forward/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active copy-forward treatment templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getAllTreatmentTemplates() {
    const response = await authFetch(`${this.treatmentBaseUrl}/all`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Treatment templates could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getAllTreatmentTemplatesByBodyPart(bodyPartTemplateId) {
    const id = requirePositiveId(
      bodyPartTemplateId,
      'body-part template ID'
    );

    const response = await authFetch(
      `${this.treatmentBaseUrl}/body-part/${id}/all`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Treatment templates for this body part could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async createTreatmentTemplate(request) {
    const response = await authFetch(this.treatmentBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Treatment template could not be created.'
      );
    }

    return data || {};
  }

  async updateTreatmentTemplate(treatmentTemplateId, request) {
    const id = requirePositiveId(
      treatmentTemplateId,
      'treatment template ID'
    );

    const response = await authFetch(`${this.treatmentBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Treatment template could not be updated.'
      );
    }

    return data || {};
  }

  async setTreatmentTemplateActive(treatmentTemplateId, active) {
    const id = requirePositiveId(
      treatmentTemplateId,
      'treatment template ID'
    );

    const response = await authFetch(
      `${this.treatmentBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Treatment template could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }


  /* ============================================================
     INSURANCE NOTE RULES
     ============================================================ */

  async getActiveInsuranceNoteRules() {
    const response = await authFetch(`${this.insuranceRuleBaseUrl}/active`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active insurance-note rules could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getInsuranceNoteRule(insuranceNoteRuleId) {
    const id = requirePositiveId(
      insuranceNoteRuleId,
      'insurance-note rule ID'
    );

    const response = await authFetch(`${this.insuranceRuleBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Insurance-note rule could not be loaded.'
      );
    }

    return data || {};
  }

  async getInsuranceNoteRuleByCode(ruleCode) {
    const code = String(ruleCode || '').trim();

    if (!code) {
      throw new Error('Insurance rule code is required.');
    }

    const response = await authFetch(
      `${this.insuranceRuleBaseUrl}/code/${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Insurance-note rule could not be loaded by code.'
      );
    }

    return data || {};
  }

  async getActiveInsuranceNoteRulesByInsurance(insuranceId) {
    const id = requirePositiveId(insuranceId, 'insurance ID');

    const response = await authFetch(
      `${this.insuranceRuleBaseUrl}/insurance/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active insurance-note rules for this insurance could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getHighestPriorityActiveRuleForInsurance(insuranceId) {
    const id = requirePositiveId(insuranceId, 'insurance ID');

    const response = await authFetch(
      `${this.insuranceRuleBaseUrl}/insurance/${id}/primary`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Primary insurance-note rule for this insurance could not be loaded.'
      );
    }

    return data || {};
  }

  async getActiveDefaultInsuranceNoteRule() {
    const response = await authFetch(
      `${this.insuranceRuleBaseUrl}/default/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'The active default insurance-note rule could not be loaded.'
      );
    }

    return data || {};
  }

  async getActivePlanOfCareRequiredRules() {
    const response = await authFetch(
      `${this.insuranceRuleBaseUrl}/plan-of-care/required`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Plan-of-Care insurance-note rules could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveSpecialTreatmentRules() {
    const response = await authFetch(
      `${this.insuranceRuleBaseUrl}/special-treatment/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Special-treatment insurance-note rules could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveTreatmentTimeRequiredRules() {
    const response = await authFetch(
      `${this.insuranceRuleBaseUrl}/treatment-time/required`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Treatment-time-required insurance-note rules could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getAllInsuranceNoteRules() {
    const response = await authFetch(`${this.insuranceRuleBaseUrl}/all`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Insurance-note rules could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async createInsuranceNoteRule(request) {
    const response = await authFetch(this.insuranceRuleBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Insurance-note rule could not be created.'
      );
    }

    return data || {};
  }

  async updateInsuranceNoteRule(insuranceNoteRuleId, request) {
    const id = requirePositiveId(
      insuranceNoteRuleId,
      'insurance-note rule ID'
    );

    const response = await authFetch(`${this.insuranceRuleBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Insurance-note rule could not be updated.'
      );
    }

    return data || {};
  }

  async setInsuranceNoteRuleActive(insuranceNoteRuleId, active) {
    const id = requirePositiveId(
      insuranceNoteRuleId,
      'insurance-note rule ID'
    );

    const action = active ? 'activate' : 'deactivate';
    const response = await authFetch(
      `${this.insuranceRuleBaseUrl}/${id}/${action}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Insurance-note rule could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }

  /* ============================================================
     PROVIDER SIGNATURES
     ============================================================ */

  async getProviderSignature(providerSignatureId) {
    const id = requirePositiveId(
      providerSignatureId,
      'provider-signature ID'
    );

    const response = await authFetch(`${this.providerSignatureBaseUrl}/${id}`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Provider signature could not be loaded.'
      );
    }

    return data || {};
  }

  async getActiveProviderSignatures() {
    const response = await authFetch(`${this.providerSignatureBaseUrl}/active`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active provider signatures could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getActiveProviderSignaturesByProvider(providerId) {
    const id = requirePositiveId(providerId, 'provider ID');

    const response = await authFetch(
      `${this.providerSignatureBaseUrl}/provider/${id}/active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Active signatures for this provider could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getPrimaryActiveProviderSignature(providerId) {
    const id = requirePositiveId(providerId, 'provider ID');

    const response = await authFetch(
      `${this.providerSignatureBaseUrl}/provider/${id}/primary`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Primary provider signature could not be loaded.'
      );
    }

    return data || {};
  }

  async hasActiveProviderSignature(providerId) {
    const id = requirePositiveId(providerId, 'provider ID');

    const response = await authFetch(
      `${this.providerSignatureBaseUrl}/provider/${id}/has-active`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Provider signature availability could not be checked.'
      );
    }

    return Boolean(data);
  }

  async getAllProviderSignatures() {
    const response = await authFetch(`${this.providerSignatureBaseUrl}/all`, {
      method: 'GET',
      headers: this.getPlainHeaders(),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Provider signatures could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async getAllProviderSignaturesByProvider(providerId) {
    const id = requirePositiveId(providerId, 'provider ID');

    const response = await authFetch(
      `${this.providerSignatureBaseUrl}/provider/${id}/all`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Signatures for this provider could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }

  async createProviderSignature(request) {
    const response = await authFetch(this.providerSignatureBaseUrl, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Provider signature could not be created.'
      );
    }

    return data || {};
  }

  async updateProviderSignature(providerSignatureId, request) {
    const id = requirePositiveId(
      providerSignatureId,
      'provider-signature ID'
    );

    const response = await authFetch(`${this.providerSignatureBaseUrl}/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Provider signature could not be updated.'
      );
    }

    return data || {};
  }

  async setProviderSignatureActive(providerSignatureId, active) {
    const id = requirePositiveId(
      providerSignatureId,
      'provider-signature ID'
    );

    const response = await authFetch(
      `${this.providerSignatureBaseUrl}/${id}/active?active=${active ? 'true' : 'false'}`,
      {
        method: 'PATCH',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        `Provider signature could not be ${active ? 'activated' : 'deactivated'}.`
      );
    }

    return data || {};
  }

  async uploadProviderSignatureImage(providerId, file) {
    const id = requirePositiveId(providerId, 'provider ID');

    if (!file) {
      throw new Error('A signature image file is required.');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await authFetch(
      `${this.providerSignatureImageBaseUrl}/upload/${id}`,
      {
        method: 'POST',
        body: formData,
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Provider signature image could not be uploaded.'
      );
    }

    if (!data?.signatureImageReference) {
      throw new Error(
        'The signature image was uploaded, but the image reference was not returned.'
      );
    }

    return data;
  }

  async getProviderSignatureImageBlob(providerSignatureId) {
    const id = requirePositiveId(
      providerSignatureId,
      'provider-signature ID'
    );

    const response = await authFetch(
      `${this.providerSignatureImageBaseUrl}/${id}`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    if (!response.ok) {
      const data = await parseResponse(response);
      throw createRequestError(
        response,
        data,
        'Provider signature image could not be loaded.'
      );
    }

    return response.blob();
  }

}

const clinicalTemplateAdminService = new ClinicalTemplateAdminService();

export default clinicalTemplateAdminService;
